import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { faCloudUploadAlt, faDownload, faFile } from '@fortawesome/free-solid-svg-icons';
import { firstValueFrom } from 'rxjs';
import { StorageKeys } from 'src/app/share/enums/storage.enum';
import { ShipmentServiceService } from 'src/app/share/services/shipment/shipment-service.service';
import { StorageService } from 'src/app/share/services/storage-service/storage.service';
import { CommonService } from '../../share/services/common/common.service';

const PART_SIZE = 5 * 1024 * 1024;
const CONCURRENCY = 3;
const MAX_RETRIES = 3;

@Component({
    selector: 'app-material-upload',
    templateUrl: './material-upload.component.html',
    styleUrl: './material-upload.component.scss',
    standalone: false
})
export class MaterialUploadComponent implements OnDestroy {

  public faCloudUploadAlt = faCloudUploadAlt;
  public faFile = faFile;
  public faDownload = faDownload;
  selectedFolder: any = '';
  uploadingFile: boolean = false;
  isUploadDocuments: boolean = true;
  isLoader: boolean = false;
  selectedFileName: string = '';
  @Input() materialData: any;
  @Output() closeOverlay = new EventEmitter<void>();
  @Output() successMessage = new EventEmitter<string>();
  selectedFile: File | null = null;
  subFolders: any;
  companyId: any;
  errorMessage: any;
  companyPrefs : any;
  scannerLicence : any;
  scanDisabled: boolean = true;
  shipmentMode:any;
  shipmentType:any;
  isScanDocument = false;
  isScannedFile = false;
  isLoading: boolean = false;
  uploadProgress = 0;
  private recordId = '';
  private uploadedParts: { partNumber: number; etag: string }[] = [];
  private totalParts = 0;
  private completedParts = 0;

  constructor(
    readonly shipmentService: ShipmentServiceService,
    readonly storageService: StorageService,
    readonly commonService: CommonService,
  ) {
    this.companyPrefs = this.storageService.getDataFromLocalStorage(StorageKeys.PREFERENCES);
    this.scannerLicence = this.storageService.getDataFromLocalStorage(StorageKeys.SCANER_LICENCE);
    this.scanDisabled = !(this.companyPrefs?.['documentVault']?.scanner && this.scannerLicence);
  }

  ngOnInit() {
    this.getSubfoldersList();
    const selfDetailsData = this.storageService.getDataFromLocalStorage(StorageKeys.SELF_DETAILS);
    if (selfDetailsData) {
      this.companyId = selfDetailsData.company;
    }
    this.commonService.getScanFiles()?.subscribe(file => {
      this.uploadingFile = file;
      if (file) {
        this.isScannedFile = true;
        let data: any = { target: { files: [] } };
        data.target.files.push(file.blob[0]);
        this.onFileSelected(data);
      }
    });
  }

  ngOnDestroy() {
    this.abortIfInProgress();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.selectedFile = file;
    this.selectedFileName = file.name;
    this.uploadingFile = true;
  }

  deleteFile() {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.uploadingFile = false;
    this.isScannedFile = false;
  }

  close() {
    if (this.isLoader) {
      this.abortIfInProgress();
    }
    this.closeOverlay.emit();
  }

  save() {
    this.isUploadDocuments = true;
    if (this.selectedFile) {
      if (this.selectedFile.size < PART_SIZE) {
        this.uploadFile();
      } else {
        this.startMultipartUpload();
      }
    }
  }

  uploadFile(): void {
    this.isLoader = true;
    if (!this.selectedFile || !this.selectedFolder) return;
    const id = this.materialData._id || this.materialData;
    const type = this.getMaterialType();
    this.shipmentService.uploadFile(this.selectedFile, id, this.companyId, this.selectedFolder, type).subscribe({
      next: (res: any) => {
        this.isLoader = false;
        this.successMessage.emit('File uploaded successfully');
        this.close();
      },
      error: (error: any) => {
        this.isLoader = false;
        this.errorMessage = error?.error?.error?.message;
      }
    });
  }

  async startMultipartUpload(): Promise<void> {
    if (!this.selectedFile || !this.selectedFolder) return;
    this.isLoader = true;
    this.uploadProgress = 0;
    this.uploadedParts = [];
    this.errorMessage = null;
    this.completedParts = 0;
    const file = this.selectedFile;
    const id = this.materialData._id || this.materialData;
    const estimatedPartCount = Math.max(1, Math.floor(file.size / PART_SIZE));
    this.totalParts = estimatedPartCount;
    try {
      const initRes: any = await firstValueFrom(
        this.shipmentService.initiateUpload(id, this.companyId, this.selectedFolder, {
          originalFileName: file.name,
          mimeType: file.type || 'application/pdf',
          declaredSize: file.size,
          parts: estimatedPartCount,
        })
      );
      this.recordId = initRes.data.recordId;
      const serverPartSize = initRes.data.partSize ?? PART_SIZE;
      const partCount = Math.max(1, Math.floor(file.size / serverPartSize));
      this.totalParts = partCount;
      const partTasks = Array.from({ length: partCount }, (_, i) => {
        const partNumber = i + 1;
        const start = i * serverPartSize;
        const end = i === partCount - 1 ? file.size : start + serverPartSize;
        const blob = file.slice(start, end);
        return () => this.uploadSinglePart(partNumber, blob);
      });
      await this.runWithConcurrency(partTasks, CONCURRENCY);
      await firstValueFrom(
        this.shipmentService.completeUpload({
          recordId: this.recordId,
          parts: [...this.uploadedParts].sort((a, b) => a.partNumber - b.partNumber),
        })
      );
      this.recordId = '';
      this.uploadProgress = 100;
      this.isLoader = false;
      this.successMessage.emit('File uploaded successfully');
      this.close();
    } catch (err: any) {
      this.isLoader = false;
      this.errorMessage = err?.error?.error?.message || err?.message || 'Upload failed. Please try again.';
    }
  }

  private async uploadSinglePart(partNumber: number, blob: Blob): Promise<void> {
    const formData = new FormData();
    formData.append('recordId', this.recordId);
    formData.append('partNumber', String(partNumber));
    formData.append('chunk', blob, 'chunk');
    const res: any = await this.retryWithBackoff(() =>
      firstValueFrom(this.shipmentService.uploadPart(formData))
    );
    const etag: string = res.data?.etag ?? res.etag ?? '';
    this.uploadedParts.push({ partNumber, etag });
    this.completedParts++;
    this.uploadProgress = Math.min(Math.round((this.completedParts / this.totalParts) * 100), 99);
  }

  private async retryWithBackoff<T>(fn: () => Promise<T>): Promise<T> {
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await fn();
      } catch (err) {
        if (attempt === MAX_RETRIES) throw err;
        await new Promise(r => setTimeout(r, Math.pow(2, attempt - 1) * 1000));
      }
    }
    throw new Error('Max retries exceeded');
  }

  private async runWithConcurrency(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
    const queue = [...tasks];
    const workers = Array(Math.min(limit, queue.length)).fill(null).map(async () => {
      while (queue.length) {
        const task = queue.shift();
        if (task) await task();
      }
    });
    await Promise.all(workers);
  }

  private abortIfInProgress(): void {
    if (this.recordId) {
      this.shipmentService.abortUpload({ recordId: this.recordId }).subscribe();
      this.recordId = '';
    }
  }

  private getMaterialType(): string {
    if (this.materialData?.selectedRegister?.key === 'Outward') return 'outbound';
    if (this.materialData?.selectedRegister?.key === 'Scrap') return 'scrap';
    return '';
  }

  getSubfoldersList() {
    const id = this.materialData?._id || this.materialData;
    const type = this.getMaterialType();
    this.shipmentService.getShipmentSubFolders(id, { type }).subscribe({
      next: (res: any) => {
        this.subFolders = res?.data?.folders;
        this.shipmentMode = res?.data?.shipmentMode;
        this.shipmentType = res?.data?.shipmentType;
        if (this.subFolders?.length) {
          this.selectedFolder = this.subFolders[0]._id;
        }
      },
      error: (error: any) => {
        console.error('Error fetching subfolders:', error);
      }
    });
  }

  isSaveDisabled(): boolean {
    return !this.selectedFile || !this.selectedFolder;
  }

  scanDocument(): void {
    this.isScanDocument = true;
  }

  closeScanDoc(file: any) {
    this.isScanDocument = false;
  }

  toTitleCase(value: string): string {
    if (!value) return '';
    return value
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

   @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.isUploadDocuments) {
        this.close();
      }
    }

  downloadFile() {
    if (!this.selectedFile) return;
    this.isLoading = true;
    setTimeout(() => {
      const url = URL.createObjectURL(this.selectedFile!);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.selectedFileName;
      a.click();
      URL.revokeObjectURL(url);
      this.isLoading = false;
    }, 2000);
  }
}
